import { styled, alpha } from '@mui/material/styles'
import { Dialog, DialogTitle, DialogContent, IconButton, Paper, Box } from '@mui/material'
import { SELF_VIEW_MIRROR_STYLE } from '../../utils/selfViewMirror'

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    overflow: 'hidden',
    maxWidth: 800,
    width: '100%',
    boxShadow: theme.shadows[10],
  },
}))

export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.default,
  backgroundImage: 'none',
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 24,
  overflowX: 'hidden',
  background: theme.palette.background.paper,
  backgroundImage: 'none',
}))

export const VideoPreviewContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  height: 320,
  borderRadius: 12,
  overflow: 'hidden',
  position: 'relative',
  // Keeps the handover underlay's negative z-index inside this box: above its
  // background, below the preview. See previewUnderlayStyle.
  isolation: 'isolate',
  backgroundColor: theme.palette.background.default,
  backgroundImage: 'none',
  boxShadow: theme.shadows[2],
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.35),
    boxShadow: theme.shadows[4],
  },
}))

export const NoVideoFeedback = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  gap: 16,
  color: theme.palette.text.secondary,
  background: theme.palette.background.default,
  backgroundImage: 'none',
}))

export const MediaControlsWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  padding: 12,
  display: 'flex',
  justifyContent: 'center',
  gap: 16,
  background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.45)}, transparent)`,
  zIndex: 2,
}))

export const MediaControlButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  backdropFilter: 'blur(4px)',
  transition: 'all 0.2s ease',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    transform: 'scale(1.05)',
    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  '&.disabled': {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    color: theme.palette.error.main,
    borderColor: alpha(theme.palette.error.main, 0.2),
  },
}))

export const DeviceSelectCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.paper,
  backgroundImage: 'none',
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  height: '100%',
  boxShadow: theme.shadows[2],
  padding: 16,
  '&:hover': {
    boxShadow: theme.shadows[4],
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
}))

/**
 * The join-screen self view, mirrored like the in-game own tile (see
 * `utils/selfViewMirror`). The transform sits on the `<video>` alone, so the
 * covers and notices over it read normally; they come after it in the preview
 * box, which keeps them above the transformed video's stacking context.
 */
export const StyledVideo = styled('video')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  ...SELF_VIEW_MIRROR_STYLE,
})

/**
 * How the preview looks while the chosen effect is not on screen: its processor
 * is starting, it is passing the raw camera through on the way from "no
 * effect", or the effect cannot be shown and the camera is held.
 *
 * The feed is hidden outright rather than blurred. A `blur(12px)` on the live
 * camera was the earlier choice, on the reasoning that a preview which resolves
 * beats one that pops in — but a 12px blur does not conceal a room, it just
 * softens it, so what the user saw was their real background arriving first and
 * the one they picked arriving second. That is the exact thing the effect is for.
 * Hidden means hidden; `previewCoverSx` puts a flat panel in its place, with
 * the progress or the problem on it.
 *
 * The element stays mounted and playing underneath — only its opacity changes —
 * so revealing it costs nothing and cannot re-trigger a decode.
 */
export const previewPreparingStyle = {
  opacity: 0,
} as const

export const previewSettledStyle = {
  opacity: 1,
} as const

/**
 * The panel shown over the preview while the chosen effect is not on screen.
 *
 * A flat themed panel and nothing else — never a frozen or blurred frame of
 * what the element last showed, which may be the room the effect hides, and
 * never a picture of the chosen background without the person in it, which is
 * not what the call will show. It is **opaque**; that is the whole point.
 */
export const previewCoverSx = {
  position: 'absolute',
  inset: 0,
  backgroundColor: 'background.default',
  color: 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  px: 3,
} as const

/**
 * Deliberately no transition.
 *
 * Fading the element in means it is partly transparent for the duration, and what
 * shows through is `VideoPreviewContainer`'s dark ground — so a "smooth" 220ms
 * opacity ramp is, visibly, a fade through black. That was the last of the
 * reported black frames.
 *
 * Nothing is lost by cutting it: the cover is replaced by the composited picture
 * in one step, and a fade would only show the dark ground between them.
 */
export const PREVIEW_EFFECT_TRANSITION = 'none'

/**
 * The second `<video>` beneath a preview that holds the raw view a pick was
 * made over while the processed stream takes the preview over (SC-158; see
 * usePreviewHandoverUnderlay). Behind everything else in the preview box, which
 * must isolate (`isolation: isolate`) so the negative z-index stays inside it.
 * While the preview above it is opaque it is simply hidden behind it; it shows
 * only while the preview is transparent because it has not painted yet.
 *
 * Mirrored, like the preview it stands in for, so nothing visibly flips when
 * the processed stream takes over.
 */
export const previewUnderlayStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
  ...SELF_VIEW_MIRROR_STYLE,
} as const

/** Bottom-edge progress bar shown over a preview while an effect is starting. */
export const previewProgressSx = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 3,
  borderBottomLeftRadius: 'inherit',
  borderBottomRightRadius: 'inherit',
} as const

export const LoadingWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  textAlign: 'center',
  gap: 16,
  color: theme.palette.text.secondary,
}))

export const PermissionStatusContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  margin: '16px 0',
  padding: 12,
  borderRadius: 8,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
}))

export const StatusIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: 'success' | 'warning' | 'error' }>(({ theme, status }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color:
    status === 'success'
      ? theme.palette.success.main
      : status === 'warning'
        ? theme.palette.warning.main
        : theme.palette.error.main,
}))
